/**
 * Net-worth report (Phase 3, §6.3). A hero tile "Aktuelles Vermögen"
 * (`current_net_worth` + delta vs the range start, with an up/down icon — never
 * colour alone) above a single brand-sky net-worth line (no legend — the title
 * names it; the axis crosses zero for negative net worth). An optional
 * per-account toggle renders a multi-line breakdown (handles negative balances).
 */
import { useState } from 'react';
import { TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { formatMonth, formatMonthShort, money } from '../../lib/format.ts';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SegmentedControl } from '../../components/ui/SegmentedControl.tsx';
import { Skeleton } from '../../components/ui/Skeleton.tsx';
import { useReportNetworth } from '../../lib/hooks/useReports.ts';
import type { MonthRange } from '../../lib/queryKeys.ts';
import { LineChart, type LineSeries } from './charts/LineChart.tsx';
import { getBucketColor, semanticColor } from './charts/palette.ts';
import { useChartMode } from './charts/useChartDims.tsx';

const toNum = (m: string): number => {
  const n = Number.parseFloat(m);
  return Number.isFinite(n) ? n : 0;
};

type View = 'gesamt' | 'konten';

export function NetWorthReport({ range }: { range: MonthRange }) {
  const mode = useChartMode();
  const [view, setView] = useState<View>('gesamt');
  const { data, isLoading, isError, error, refetch } = useReportNetworth(range);

  if (isLoading) return <Skeleton className="h-80 w-full" />;
  if (isError) {
    return (
      <ErrorState title="Vermögen konnte nicht geladen werden" error={error} onRetry={refetch} />
    );
  }
  if (!data || data.accounts.length === 0) {
    return (
      <EmptyState
        icon={Wallet}
        title="Keine Kontostände"
        description="Sobald Kontosalden vorliegen, erscheint hier der Vermögensverlauf."
      />
    );
  }

  const delta = toNum(data.delta);
  const categories = data.points.map((p) => formatMonthShort(p.month));

  const totalSeries: LineSeries[] = [
    {
      key: 'networth',
      label: 'Vermögen',
      color: semanticColor('netWorth', mode),
      values: data.points.map((p) => toNum(p.net_worth)),
    },
  ];

  const accountSeries: LineSeries[] = data.accounts.map((acc, i) => ({
    key: String(acc.account_id),
    label: acc.name,
    color: getBucketColor(null, mode, i),
    values: acc.values.map(toNum),
  }));

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
            Aktuelles Vermögen
          </p>
          <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-900 dark:text-slate-100 sm:text-4xl">
            {money(data.current_net_worth)}
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          {delta >= 0 ? (
            <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
          ) : (
            <TrendingDown className="h-4 w-4 text-rose-600 dark:text-rose-400" aria-hidden />
          )}
          <AmountText value={data.delta} showPlus />
          <span className="text-slate-400 dark:text-slate-500">
            seit {formatMonth(range.from)}
          </span>
        </div>
      </div>

      <div>
        {data.accounts.length > 1 ? (
          <div className="mb-4 flex justify-end">
            <SegmentedControl<View>
              value={view}
              onChange={setView}
              ariaLabel="Darstellung"
              options={[
                { value: 'gesamt', label: 'Gesamt' },
                { value: 'konten', label: 'Nach Konto' },
              ]}
            />
          </div>
        ) : null}

        {view === 'konten' && data.accounts.length > 1 ? (
          <LineChart
            title="Vermögen nach Konto"
            subtitle="Kontosalden im Zeitverlauf"
            categories={categories}
            series={accountSeries}
            includeZero
          />
        ) : (
          <LineChart
            title="Vermögensverlauf"
            subtitle="Gesamtvermögen über den Zeitraum"
            categories={categories}
            series={totalSeries}
            showLegend={false}
            directLabelLast
            includeZero
          />
        )}
      </div>
    </div>
  );
}
